import { OnInit, OnDestroy } from '@angular/core';
import { Component } from '@angular/core';
import { UserTypeService } from '../common/service/user-type.service';
import { ReturnListService } from '../fednow/service/http/return-list.service';
import { IMenu } from './interface/menu.interface';
import { IInstitutionConfiguration } from '../fednow/interfaces/institution/institution-configuration.interface';
import { AbstractInstitutionService } from '../fednow/service/abstraction/abstract.institution.service';
import { AbstractConfigurationPendingChangesService } from '../fednow/service/abstraction/abstract.configuration-pending-changes.service';
import { AbstractConfigurationReviewStatusService } from '../fednow/service/abstraction/abstract.configuration-review-status.service';
import { AbstractTransactionDualApprovalService } from '../fednow/service/abstraction/abstract.transaction-dual-approval.service';
import { IDecisionedNotificationCountByTransactionType } from '../fednow/interfaces/transaction-dual-approval/decisioned-notification-count-by-transaction-type.interface';
import { MenuService, MenuServiceEvent } from './service/app.menu.service';
import { EMPTY, Subscription, catchError, exhaustMap, interval, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { BadgeCountService } from './service/badge-count.service';
import { IMenuItem } from './interface/menuitem.interface';

@Component({
  selector: 'app-menu',
  templateUrl: './app.menu.component.html',
})
export class AppMenuComponent implements OnInit, OnDestroy {
  private readonly DUAL_APPROVAL_BADGE_POLLING_INTERVAL_MS = 30000;
  private readonly DECISIONED_BADGE_POLLING_INTERVAL_MS = 60000;

  model: IMenu[] = [];
  returnListCount: number = 0;
  configuration: IInstitutionConfiguration | undefined;
  fedNowMenu: IMenu | undefined;
  configurationMenu: IMenu | undefined;
  private pollingSubscription: Subscription | undefined;
  private decisionedPollingSubscription: Subscription | undefined;
  private menuEventsSubscription = new Subscription();
  private batchDualApprovalPollingSubscription: Subscription | undefined;
  private batchDecisionedPollingSubscription: Subscription | undefined;
  private destroyed = false;

  constructor(
    private userTypeService: UserTypeService,
    private returnListService: ReturnListService,
    private institutionService: AbstractInstitutionService,
    private configurationPendingChangesService: AbstractConfigurationPendingChangesService,
    private configurationReviewStatusService: AbstractConfigurationReviewStatusService,
    private transactionDualApprovalService: AbstractTransactionDualApprovalService,
    private menuService: MenuService,
    private badgeCountService: BadgeCountService
  ) {}

  async loadConfiguration() {
    this.configuration = await this.institutionService.getConfiguration();
  }

  async ngOnInit() {
    await this.loadMenu();

    if (this.destroyed) {
      return;
    }

    this.menuServiceSubscription();
    this.startBatchDualApprovalPolling();
    this.startBatchDecisionedPolling();
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.pollingSubscription?.unsubscribe();
    this.decisionedPollingSubscription?.unsubscribe();
    this.menuEventsSubscription.unsubscribe();
    this.batchDualApprovalPollingSubscription?.unsubscribe();
    this.batchDecisionedPollingSubscription?.unsubscribe();
  }

  private async loadMenu() {
    await this.loadConfiguration();

    if (this.destroyed) {
      return;
    }

    var isCorrespondent = this.userTypeService.isCorrespondent();
    var isStandAlone = this.userTypeService.isStandAlone();
    var beTreatedLikeRespondent = !isCorrespondent || (isCorrespondent && isStandAlone);
    var isDashboardAllowed = this.configuration?.isDashboardAllowed;
    var isNotificationAllowed = this.configuration?.isNotificationAllowed;
    var isEnabledForRtp = this.configuration?.isEnabledForRtp ?? false;
    var isEnabledForFedNow =
      this.configuration?.isOnboardedLiveAndView || this.configuration?.isConfigurationAllowed;
    this.model = [];

    if (isDashboardAllowed) {
      this.model.push(this.getSummaryMenu());
    }

    if (isEnabledForFedNow) {
      this.fedNowMenu = this.getFedNowMenu(!beTreatedLikeRespondent);
      this.model.push(this.fedNowMenu);
    }

    if (isEnabledForRtp) {
      this.model.push(this.getRTPMenu(isCorrespondent, !beTreatedLikeRespondent));
    }

    if (this.configuration?.isConfigurationAllowed) {
      this.configurationMenu = this.getConfigurationMenu();
      this.model.push(this.configurationMenu);
    }

    if (isNotificationAllowed) {
      this.model.push(this.getNotificationsMenu());
    }

    this.loadBadges();
  }

  /**
   * Starts the polling process to update the FedNow Dual Approval count badge at regular intervals.
   * This ensures that the user sees an up-to-date count of pending dual approvals without needing to
   * refresh the page or navigate away and back to the menu.
   */
  private startDualApprovalPolling() {
    if (!this.configuration?.isFedNowDualApprovalAllowed) {
      return;
    }

    this.pollingSubscription?.unsubscribe();

    this.pollingSubscription = interval(this.DUAL_APPROVAL_BADGE_POLLING_INTERVAL_MS).subscribe(
      () => {
        this.loadFedNowDualApprovalBadge(true).catch(error => {
          console.error('Error polling count for FedNow dual approval badge:', error);
        });
      }
    );
  }

  /**
   * Starts background polling for the Transactions badge, which reflects decisioned
   * dual approval notifications for the current user.
   */
  private startDecisionedBadgePolling() {
    if (
      !this.configuration?.isFedNowDualApprovalAllowed &&
      !this.configuration?.isRFPReceiveAllowed
    ) {
      return;
    }

    this.decisionedPollingSubscription?.unsubscribe();

    this.decisionedPollingSubscription = interval(
      this.DECISIONED_BADGE_POLLING_INTERVAL_MS
    ).subscribe(() => {
      this.loadDecisionedBadges(true).catch(error => {
        console.error('Error polling decisioned counts for FedNow badges:', error);
      });
    });
  }

  private startBatchDualApprovalPolling() {
    this.batchDualApprovalPollingSubscription?.unsubscribe();
    if (this.getBatchBadgeItems(30).length === 0) {
      return;
    }

    this.batchDualApprovalPollingSubscription = interval(
      this.DUAL_APPROVAL_BADGE_POLLING_INTERVAL_MS
    )
      .pipe(exhaustMap(() => this.refreshBatchBadgeCounts(30)))
      .subscribe();
  }

  private startBatchDecisionedPolling() {
    this.batchDecisionedPollingSubscription?.unsubscribe();
    if (this.getBatchBadgeItems(60).length === 0) {
      return;
    }

    this.batchDecisionedPollingSubscription = interval(this.DECISIONED_BADGE_POLLING_INTERVAL_MS)
      .pipe(exhaustMap(() => this.refreshBatchBadgeCounts(60)))
      .subscribe();
  }

  private refreshBatchBadgeCounts(intervalSeconds: 30 | 60) {
    const items = this.getBatchBadgeItems(intervalSeconds);
    const menuItems = items.flatMap(item => (item.badgeKey ? [item.badgeKey] : []));
    if (menuItems.length === 0) {
      return EMPTY;
    }

    const getCounts =
      intervalSeconds === 30
        ? this.badgeCountService.getDualApprovalCounts(menuItems)
        : this.badgeCountService.getDecisionedCounts(menuItems);

    return getCounts.pipe(
      tap(response => {
        const currentItems = this.getBatchBadgeItems(intervalSeconds);
        for (const item of items) {
          if (!item.badgeKey || !currentItems.includes(item)) {
            continue;
          }

          const count = response.counts[item.badgeKey];
          if (typeof count === 'number') {
            item.badge = count;
          } else if (count) {
            item.badge = count.approved + count.denied;
          } else {
            continue;
          }

          item.badgeSeverity = 'primary';
          item.pulseBadgeOnDisplay = true;
        }
      }),
      catchError(error => {
        console.error(`Error polling menu badge counts every ${intervalSeconds} seconds:`, error);
        return EMPTY;
      })
    );
  }

  private getBatchBadgeItems(intervalSeconds: 30 | 60): IMenuItem[] {
    const eligibleItems: IMenuItem[] = [];
    const visit = (items: IMenuItem[]) => {
      for (const item of items) {
        if (item.visible === false || item.disabled) {
          continue;
        }

        if (item.items) {
          visit(item.items);
        }

        if (!item.badgeKey || item.badgePollingIntervalSeconds !== intervalSeconds) {
          continue;
        }

        if (!this.isBadgeCountEligible(item.badgeKey)) {
          continue;
        }

        eligibleItems.push(item);
      }
    };

    visit(this.model);
    return eligibleItems;
  }

  private isBadgeCountEligible(badgeKey: NonNullable<IMenuItem['badgeKey']>): boolean {
    switch (badgeKey) {
      case 'rtp-ct':
        return this.configuration?.isRtpDualApprovalAllowed ?? false;
      case 'fednow-ct':
      case 'fednow-rfp-received':
        return this.configuration?.isFedNowDualApprovalAllowed ?? false;
      default:
        return true;
    }
  }

  private getSummaryMenu(): IMenu {
    return {
      label: 'Summary',
      icon: 'pi pi-fw pi-table',
      items: [
        {
          label: 'Summary Information',
          visible: this.configuration?.isDashboardAllowed,
          icon: 'pi pi-fw pi-home',
          routerLink: ['/dashboard/summary-information'],
        },
      ],
    };
  }

  private getNotificationsMenu(): IMenu {
    return {
      label: 'Notifications',
      icon: 'pi pi-fw pi-bell',
      items: [
        {
          label: 'Notifications',
          icon: 'pi pi-fw pi-bell',
          routerLink: ['/notifications'],
        },
      ],
    };
  }

  private getFedNowMenu(isCorrespondent: boolean): IMenu {
    return {
      label: 'FedNow',
      icon: 'pi pi-fw pi-table',
      items: [
        {
          label: 'Account Activity',
          visible: this.configuration?.isAccountActivityAllowed,
          icon: 'pi pi-fw pi-table',
          routerLink: ['/fednow/account-activity'],
        },
        {
          label: 'Transactions',
          icon: 'pi pi-fw pi-arrow-right-arrow-left',
          visible: !isCorrespondent && (this.configuration?.isTransactionAllowed ?? false),
          routerLink: ['/fednow/transaction'],
          badgeKey: 'fednow-ct',
          badgePollingIntervalSeconds: 60,
        },
        {
          label: 'Send Payment',
          icon: 'pi pi-fw pi-dollar',
          visible: !isCorrespondent && (this.configuration?.isSendPaymentAllowed ?? false),
          routerLink: ['/fednow/send-payment'],
        },
        {
          label: 'Dual Approval',
          icon: 'pi pi-fw pi-check-circle',
          visible: !isCorrespondent && (this.configuration?.isFedNowDualApprovalAllowed ?? false),
          routerLink: ['/fednow/dual-approval'],
          badgeKey: 'fednow-dualapproval',
          badgePollingIntervalSeconds: 30,
        },
        {
          label: 'Return List',
          icon: 'pi pi-fw pi-undo',
          visible: !isCorrespondent && (this.configuration?.isReturnListAllowed ?? false),
          routerLink: ['/fednow/return-list'],
          badge: this.returnListCount,
        },
        {
          label: 'Return Initiated',
          icon: 'pi pi-fw pi-refresh',
          visible: !isCorrespondent && (this.configuration?.isReturnInitiatedAllowed ?? false),
          routerLink: ['/fednow/return-initiated-list'],
        },
        {
          label: 'RFP Received',
          icon: 'pi pi-fw pi-envelope',
          visible: !isCorrespondent && (this.configuration?.isRFPReceiveAllowed ?? false),
          routerLink: ['/fednow/rfp-list'],
          badgeKey: 'fednow-rfp-received',
          badgePollingIntervalSeconds: 60,
          badge: 0,
        },
        {
          label: 'Generate RFP',
          icon: 'pi pi-fw pi-file',
          visible: !isCorrespondent && (this.configuration?.isRFPSendAllowed ?? false),
          routerLink: ['/fednow/rfp-send'],
        },
        {
          label: 'RFP Sent',
          icon: 'pi pi-fw pi-tag',
          visible: !isCorrespondent && (this.configuration?.isRFPSentListAllowed ?? false),
          routerLink: ['/fednow/rfp-sent'],
        },
        {
          label: 'Reject Analysis',
          icon: 'pi pi-fw pi-chart-bar',
          routerLink: ['/fednow/reject-analysis'],
        },
      ],
    };
  }

  private getConfigurationMenu(): IMenu {
    return {
      label: 'Configuration',
      icon: 'pi pi-fw pi-cog',
      visible: this.configuration?.isConfigurationAllowed ?? false,
      items: [
        {
          label: 'Settings',
          icon: 'pi pi-fw pi-book',
          visible: this.configuration?.isConfigurationAllowed ?? false,
          routerLink: ['/fednow/configuration'],
        },
        {
          label: 'Pending Reviews',
          icon: 'pi pi-fw pi-comments',
          visible: this.configuration?.isConfigurationPendingReviewAllowed ?? false,
          routerLink: ['/fednow/configuration/pending-changes'],
        },
        {
          label: 'Review Status',
          icon: 'pi pi-fw pi-comment',
          visible: this.configuration?.isConfigurationReviewStatusAllowed ?? false,
          routerLink: ['/fednow/configuration/pending-changes-review-status'],
        },
        {
          label: 'Audit Review',
          icon: 'pi pi-fw pi-bookmark',
          visible: this.configuration?.isConfigurationAllowed ?? false,
          routerLink: ['/fednow/configuration/audit-review'],
        },
      ],
    };
  }

  private getRTPMenu(isCorrespondent: boolean, isSettlementOnlyCorrespondent: boolean): IMenu {
    const isRtpDualApprovalAllowed =
      !isCorrespondent && (this.configuration?.isRtpDualApprovalAllowed ?? false);

    return {
      label: 'RTP',
      icon: 'pi pi-fw pi-table',
      items: [
        {
          label: 'Account Activity',
          icon: 'pi pi-fw pi-history',
          routerLink: ['/rtp/account-activity'],
        },
        {
          label: 'Reconciliation',
          visible: isCorrespondent,
          icon: 'pi pi-fw pi-list',
          routerLink: ['/rtp/correspondent-reconciliation'],
        },
        {
          label: 'Reconciliation',
          visible: !isCorrespondent,
          icon: 'pi pi-fw pi-list',
          routerLink: ['/rtp/respondent-reconciliation'],
        },
        {
          label: 'Transactions',
          visible:
            !isSettlementOnlyCorrespondent &&
            (this.configuration?.isEnabledForRtpTransactions ?? false),
          icon: 'pi pi-fw pi-arrow-right-arrow-left',
          routerLink: ['/rtp/rtp-transaction'],
          badgeKey: 'rtp-ct',
          badgePollingIntervalSeconds: 60,
        },
        {
          label: 'Send Payment',
          visible:
            !isSettlementOnlyCorrespondent &&
            (this.configuration?.isEnabledForRtpSendPayment ?? false),
          icon: 'pi pi-fw pi-send',
          routerLink: ['/rtp/send-payment'],
        },
        {
          label: 'Dual Approval',
          visible: isRtpDualApprovalAllowed,
          icon: 'pi pi-fw pi-check-circle',
          routerLink: ['/rtp/dual-approval'],
          badgeKey: 'rtp-dualapproval',
          badgePollingIntervalSeconds: 30,
        },
        {
          label: 'Return List',
          visible:
            !isSettlementOnlyCorrespondent &&
            (this.configuration?.isEnabledForRtpReturnRequest ?? false),
          icon: 'pi pi-fw pi-undo',
          routerLink: ['/rtp/return-request-list'],
        },
        {
          label: 'Return Initiated',
          visible:
            !isSettlementOnlyCorrespondent &&
            (this.configuration?.isEnabledForRtpReturnInitiated ?? false),
          icon: 'pi pi-fw pi-refresh',
          routerLink: ['/rtp/return-initiated-list'],
        },
      ],
    };
  }

  /**
   * Loads all menu item badges that should be enabled per configuration, and starts the necessary polling
   * intervals for those that auto-refresh.
   */
  private async loadBadges() {
    if (this.configuration?.isConfigurationAllowed) {
      this.loadDualControlPendingChangesBadge();
      this.loadDualControlReviewStatusPendingBadge();
    }

    if (!environment.useMock && this.configuration?.isFedNowDualApprovalAllowed) {
      this.loadFedNowDualApprovalBadge();
      this.loadDecisionedBadges();
    }
  }

  /**
   * Fetches the current count of pending FedNow dual approvals and updates the "Dual Approval"
   * menu item badge.
   * @param isBackgroundPolling Indicates whether this is a request that is being performed
   * during a background polling operation. This will be false for the initial call when
   * the page is first loading.
   */
  private async loadFedNowDualApprovalBadge(isBackgroundPolling: boolean = false) {
    const dualApprovalItem = this.fedNowMenu?.items?.find(x => x.label === 'Dual Approval');

    if (!dualApprovalItem) {
      console.error('Dual Approval menu item not found while trying to update badge.');
      return;
    }

    let options;

    if (isBackgroundPolling) {
      dualApprovalItem.pulseBadgeOnDisplay = true;
      options = { skipLoading: true };
    }

    var pendingApprovalCountResult =
      await this.transactionDualApprovalService.getPendingFedNowTransactionApprovalCount(options);

    dualApprovalItem.badge = pendingApprovalCountResult?.count ?? 0;
  }

  /**
   * Fetches decisioned notification counts grouped by transaction type and approval status,
   * then applies them to the Transactions and RFP Received badges.
   * @param isBackgroundPolling Indicates whether this refresh is happening on the polling interval.
   */
  private async loadDecisionedBadges(isBackgroundPolling: boolean = false) {
    let options;

    if (isBackgroundPolling) {
      options = { skipLoading: true };
    }

    const counts =
      (await this.transactionDualApprovalService.getDecisionedNotificationCountsByTransactionType(
        options
      )) ?? [];

    this.updateDecisionedBadge('Transactions', 'Credit Transfer Send', counts, isBackgroundPolling);
    this.updateDecisionedBadge('RFP Received', 'RFP Decision', counts, isBackgroundPolling);
  }

  private updateDecisionedBadge(
    menuLabel: string,
    transactionType: string,
    counts: IDecisionedNotificationCountByTransactionType[],
    isBackgroundPolling: boolean
  ) {
    const menuItem = this.fedNowMenu?.items?.find(x => x.label === menuLabel);

    if (!menuItem) {
      return;
    }

    if (isBackgroundPolling) {
      menuItem.pulseBadgeOnDisplay = true;
    }

    const matchedCounts = counts.filter(x => x.transactionType === transactionType);
    const totalCount = matchedCounts.reduce((total, item) => total + (item.count ?? 0), 0);

    menuItem.badge = totalCount;
    menuItem.badgeSeverity = 'primary';
  }

  private async loadDualControlPendingChangesBadge() {
    var dualControlPendingChangesCount = await this.configurationPendingChangesService.getCount();
    const pendingReviewsItem = this.configurationMenu?.items?.find(
      x => x.label === 'Pending Reviews'
    );
    if (pendingReviewsItem) pendingReviewsItem.badge = dualControlPendingChangesCount?.count ?? 0;
  }

  private async loadDualControlReviewStatusPendingBadge() {
    var dualControlReviewStatusCount = await this.configurationReviewStatusService.getCount();
    const reviewStatusItem = this.configurationMenu?.items?.find(x => x.label === 'Review Status');
    if (reviewStatusItem) reviewStatusItem.badge = dualControlReviewStatusCount?.count ?? 0;
  }

  private menuServiceSubscription() {
    // Register event subscription for refreshing the badge on the FedNow Dual Control menu item.
    this.menuEventsSubscription.add(
      this.menuService.onEvent(MenuServiceEvent.UpdateBadgePendingChanges).subscribe(() => {
        this.loadDualControlPendingChangesBadge();
      })
    );

    // Register event subscription for refreshing the badge on the FedNow Pending Reviews menu item.
    this.menuEventsSubscription.add(
      this.menuService.onEvent(MenuServiceEvent.UpdateBadgeReviewStatus).subscribe(() => {
        this.loadDualControlReviewStatusPendingBadge();
      })
    );

    // Register event subscription for refreshing the badge on the FedNow Dual Approval menu item.
    this.menuEventsSubscription.add(
      this.menuService.onEvent(MenuServiceEvent.UpdateBadgeFedNowDualApproval).subscribe(() => {
        this.loadFedNowDualApprovalBadge();
      })
    );

    // Register event subscription for refreshing the badge on the FedNow Transactions menu item.
    this.menuEventsSubscription.add(
      this.menuService
        .onEvent(MenuServiceEvent.UpdateBadgeFedNowTransactionDecisioned)
        .subscribe(() => {
          this.loadDecisionedBadges();
        })
    );

    // Register event subscription for refreshing the FedNow RFP Received menu item.
    this.menuEventsSubscription.add(
      this.menuService.onEvent(MenuServiceEvent.UpdateBadgeFedNowRfpDecisioned).subscribe(() => {
        this.loadDecisionedBadges();
      })
    );

    // Register event subscription for refreshing the FedNow menu items.
    this.menuEventsSubscription.add(
      this.menuService.onEvent(MenuServiceEvent.UpdateMenu).subscribe(async () => {
        await this.loadMenu();
      })
    );
  }
}
